import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { checkoutStatus, getSubscriptionStatus } from "@/lib/api";

export default function PaymentResult({ cancelled }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "polling"); // polling | paid | expired | failed | timeout | cancelled
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await checkoutStatus(sessionId);
        if (r.payment_status === "paid") {
          setPlan(r.plan); setState("paid");
          getSubscriptionStatus().catch(() => {});
          return;
        }
        if (r.status === "expired" || r.payment_status === "failed") { setState(r.status === "expired" ? "expired" : "failed"); return; }
      } catch { /* retry */ }
      if (attempts.current >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  const planName = plan === "onepay" ? "ONE PAY" : "CHALANITO";
  const views = {
    polling: { icon: <Loader2 size={44} className="animate-spin text-[#ccff00]" />, title: "Confirmando tu pago…", body: "Estamos verificando con Stripe. Esto toma unos segundos." },
    paid: { icon: <CheckCircle2 size={44} className="text-[#ccff00]" />, title: `¡Listo! Tu plan ${planName} está activo.`, body: plan === "onepay" ? "Tienes un año completo. Conecta tu pistola de escaneo y empieza a cobrar." : "Ya puedes hablarle a Chalán y cobrar por voz." },
    expired: { icon: <XCircle size={44} className="text-[#ff5a00]" />, title: "La sesión de pago expiró", body: "No se hizo ningún cargo. Vuelve a intentarlo." },
    failed: { icon: <XCircle size={44} className="text-[#ff5a00]" />, title: "El pago no se completó", body: "Tu banco rechazó el cargo o se canceló. Puedes intentar con otra tarjeta o pagar por SPEI." },
    timeout: { icon: <Loader2 size={44} className="text-white/50" />, title: "Seguimos verificando", body: "El banco tarda un poco. Revisa en Pagar servicio en unos minutos; si el cargo se hizo, el plan se activa solo." },
    cancelled: { icon: <XCircle size={44} className="text-white/50" />, title: "Pago cancelado", body: "No se hizo ningún cargo. Puedes elegir otra forma de pago." },
  };
  const v = views[state];

  return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid={`payment-result-${state}`}>
      <div className="grain-overlay" />
      <div className="relative w-full max-w-lg border border-white/10 rounded-3xl bg-white/[0.02] backdrop-blur-xl p-8 md:p-12 text-center">
        <div className="flex justify-center mb-6">{v.icon}</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter leading-tight" data-testid="payment-result-title">{v.title}</h1>
        <p className="text-white/60 mt-3 text-sm">{v.body}</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {state === "paid" ? (
            <>
              <Link to="/cobrar" className="btn-primary justify-center" data-testid="payment-result-cobrar">Ir a Cobrar</Link>
              {plan === "onepay" && <Link to="/guia" className="px-5 py-3 rounded-full border border-white/15 text-sm hover:bg-white hover:text-black transition-colors" data-testid="payment-result-guia">Ver guía de la pistola</Link>}
            </>
          ) : (
            <>
              <Link to="/pagar" className="btn-primary justify-center" data-testid="payment-result-retry">Volver a Pagar servicio</Link>
              <Link to="/dashboard" className="px-5 py-3 rounded-full border border-white/15 text-sm hover:bg-white hover:text-black transition-colors">Ir al panel</Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
